import { gql } from "@apollo/client";

export const QUERY_USER = gql`
  query user($username: String!) {
    userByUsername(username: $username) {
      _id
      username
    }
  }
`;

export const GET_CURRENT_USER = gql`
  query currUser {
    getCurrentUser {
      _id
      firstName
      lastName
      email
      phoneNumberIds {
        _id
        phoneNumber
        numberType
        title
        recentTime
        textMessages {
          body
          sentReceivedTime
          outgoingBool
          twilioSID
          attachedImgs
        }
        unreadMessages
        hasTexts
      }
      permission
      initLogin
      failedLogins
      streetAddress
      city
      state
      zipCode
      refinReqInv {
        reqItemId
        reqItemCount
      }
      active
      userEmailAddress
      hourlyWage
      payPercentage
      defaultColor
    }
  }
`;

export const QUERY_REVIEWS = gql`
  query getReviews {
    getReviews {
      _id
      customerName
      starRating
      review
    }
  }
`;

export const QUERY_CLIENT_PHOTOS = gql`
  query GetImageById($pictureIdArr: [String]) {
    getImageById(pictureIdArr: $pictureIdArr) {
      _id
      beforeOrAfter
      s3ImageUrl
      s3ThumbUrl
      dateEntered
      note
    }
  }
`;

// `QUERY_CLIENT_PHOTOS_WITH_DATA` was the base64 (`imageData`) variant for the
// EmailModal inbox. The 2026-06-26 S3 read-swap (`f5494620`) dropped `imageData`
// from it, leaving it field-for-field identical to `QUERY_CLIENT_PHOTOS` above
// with a comment that still warned against migrating it — removed 2026-09-15 and
// its one consumer (EmailModal) pointed at `QUERY_CLIENT_PHOTOS`. No image
// display query fetches base64; rotate re-derives from the S3 bytes client-side.

export const QUERY_RECENT_BA_GROUPS = gql`
  query GetRecentBAGs(
    $localTimeEpoch: String
    $page: String
    $count: String
    $useRecents: Boolean
  ) {
    getRecentBAGs(
      localTimeEpoch: $localTimeEpoch
      page: $page
      count: $count
      useRecents: $useRecents
    ) {
      pageNum
      nextPage
      useRecents
      count
      BAGData {
        _id
        beforeId {
          _id
          beforeOrAfter
          dateEntered
          s3ImageUrl
          s3ThumbUrl
        }
        afterId {
          _id
          beforeOrAfter
          dateEntered
          s3ImageUrl
          s3ThumbUrl
        }
        weekGalleryDisplay
        dateEntered
        refinishers
        stainColor
      }
    }
  }
`;

export const QUERY_MORE_BA_GROUPS = gql`
  query GetMoreDoors($localTimeEpoch: String) {
    getMoreDoors(localTimeEpoch: $localTimeEpoch) {
      _id
      beforeId {
        _id
        beforeOrAfter
        dateEntered
        s3ImageUrl
        s3ThumbUrl
      }
      afterId {
        _id
        beforeOrAfter
        dateEntered
        s3ImageUrl
        s3ThumbUrl
      }
      weekGalleryDisplay
      dateEntered
      refinishers
      stainColor
    }
  }
`;

export const QUERY_FINEST_JOBS = gql`
  query getFinestJobs {
    getFinestJobs {
      _id
      BAGroupIds {
        _id
        beforeId {
          _id
          s3ImageUrl
          s3ThumbUrl
          beforeOrAfter
          dateEntered
        }
        afterId {
          _id
          s3ImageUrl
          s3ThumbUrl
          beforeOrAfter
          dateEntered
        }
        weekGalleryDisplay
        dateEntered
        refinishers
        stainColor
      }
    }
  }
`;

export const GET_ONE_BAG = gql`
  query getBAGById($selectedId: String!) {
    getBAGById(selectedId: $selectedId) {
      _id
      beforeId {
        _id
        s3ImageUrl
        s3ThumbUrl
        beforeOrAfter
        dateEntered
      }
      afterId {
        _id
        s3ImageUrl
        s3ThumbUrl
        beforeOrAfter
        dateEntered
      }
      weekGalleryDisplay
      dateEntered
      refinishers
      stainColor
    }
  }
`;

// Inventory hardening (2026-09-18): includeInactive is honoured for admins
// (soft-deleted rows for Show inactive / Restore). currentQuantity + price
// are cached values maintained by the movement ledger; price is per stock
// unit. The refinisher-facing RefinUpdateList calls this WITHOUT the arg.
export const GET_SHOP_INVENTORY = gql`
  query GetShopInventory($includeInactive: Boolean) {
    getInventory(includeInactive: $includeInactive) {
      id
      itemName
      currentQuantity
      maxQuantity
      price
      defaultTechQty
      stockUnit
      piecesPerUnit
      pieceName
      active
      deletedAt
      vendorSkus {
        vendor
        sku
        unitsPerPurchase
        lastSeenAt
      }
    }
  }
`;

// Shopping list — admin-only, Buy | Fill (plan D20).
//   buyQty  = max(0, requested - current)          minimum to satisfy open requests
//   fillQty = max(0, requested + (max - current))  buy plus restore the shop to max
// Both come back per row; the mode toggle, hiding zero rows and the per-mode
// sort are client-side (no refetch). Pack fields present only when the item's
// vendor SKU has a pack size > 1. Orphans carry a resolved name and are never costed.
export const GET_SHOPPING_LIST = gql`
  query GetShoppingList {
    getShoppingList {
      items {
        id
        itemName
        currentQty
        totalRequested
        maxQty
        buyQty
        fillQty
        buyPurchaseUnits
        fillPurchaseUnits
        purchaseUnitLabel
        unitCostCents
      }
      orphans {
        reqItemId
        itemName
        totalRequested
        requestingTechs {
          userId
          firstName
          lastName
          qty
        }
      }
      buyCostCents
      fillCostCents
    }
  }
`;

// Movement ledger rows for one item (history drawer). Admin only.
export const GET_INVENTORY_MOVEMENTS = gql`
  query GetInventoryMovements($itemId: String, $userId: String, $type: String, $dateStart: String, $dateEnd: String, $limit: Int, $cursor: String) {
    getInventoryMovements(itemId: $itemId, userId: $userId, type: $type, dateStart: $dateStart, dateEnd: $dateEnd, limit: $limit, cursor: $cursor) {
      items {
        id
        type
        itemId
        itemName
        qtyDelta
        qtyBefore
        qtyAfter
        actorName
        subjectName
        unitCostCents
        lineTotalCents
        costSource
        reason
        note
        occurredAt
      }
      nextCursor
    }
  }
`;

export const GET_INVENTORY_PRICE_HISTORY = gql`
  query GetInventoryPriceHistory($itemId: String!, $dateStart: String, $dateEnd: String) {
    getInventoryPriceHistory(itemId: $itemId, dateStart: $dateStart, dateEnd: $dateEnd) {
      id
      priceCents
      observedAt
      source
      vendor
    }
  }
`;

// The normal list. It never carries `searchTerm`: during a deploy the new
// bundle is served while the old server still answers, and an unknown argument
// fails the whole query — so only searching may depend on the new server.
export const GET_INC_EMAILS = gql`
  query GetIncEmails(
    $getArchived: [Boolean]
    $page: String
    $limit: String
    $userEmailAddress: String
    $showAll: Boolean
  ) {
    getIncEmails(
      getArchived: $getArchived
      page: $page
      limit: $limit
      userEmailAddress: $userEmailAddress
      showAll: $showAll
    ) {
      emails {
        attachments
        dateCreated
        emailSubject
        flag
        emailRead
        from
        id
        to
        chainedEmailIds
      }
      totalDoc
    }
  }
`;

// The database search (3+ letters): newest 200 matches, totalDoc = all matches.
export const GET_INC_EMAILS_SEARCH = gql`
  query GetIncEmailsSearch(
    $getArchived: [Boolean]
    $searchTerm: String
    $userEmailAddress: String
    $showAll: Boolean
  ) {
    getIncEmails(
      getArchived: $getArchived
      searchTerm: $searchTerm
      userEmailAddress: $userEmailAddress
      showAll: $showAll
    ) {
      emails {
        attachments
        dateCreated
        emailSubject
        flag
        emailRead
        from
        id
        to
        chainedEmailIds
      }
      totalDoc
    }
  }
`;

// Archived view, "whole thread": the ids the confirm counts and sends back.
export const GET_EMAIL_THREAD_IDS = gql`
  query GetEmailThreadIds($emailId: String!, $userEmailAddress: String, $showAll: Boolean) {
    getEmailThreadIds(emailId: $emailId, userEmailAddress: $userEmailAddress, showAll: $showAll)
  }
`;

/**
 * Quote requests from the public site, last 7 days.
 *
 * TWO documents against ONE field, deliberately: `IncEmailAndCount` lets the
 * tile ask for the count alone and the modal ask for the list, so there is no
 * `withList`-style argument deciding whether a field is populated, and no
 * ambiguity between "you didn't ask" and "there are none".
 *
 * The cache needs `typePolicies: { Query: { fields: { getQuoteRequests: { merge: true } } } }`
 * (set in App.js). `IncEmailAndCount` has no `id`, so it is not normalised and
 * the default field merge REPLACES — without it, a tile refetch writing
 * `{ totalDoc }` would evict the `emails` the modal had written.
 */
export const GET_QUOTE_REQUEST_COUNT = gql`
  query GetQuoteRequestCount {
    getQuoteRequests {
      totalDoc
    }
  }
`;

export const GET_QUOTE_REQUESTS = gql`
  query GetQuoteRequests {
    getQuoteRequests {
      totalDoc
      emails {
        id
        emailSubject
        from
        dateCreated
        emailRead
        flag
        attachments
      }
    }
  }
`;

export const GET_CLIENT_EMAIL_COUNTS = gql`
  query GetClientEmailCounts($clientEmails: [String!]!) {
    getClientEmailCounts(clientEmails: $clientEmails) {
      unread
      flagged
      urgent
    }
  }
`;

export const GET_CLIENT_ACTIVITY = gql`
  query GetClientActivity($clientId: String!, $cursor: String, $limit: Int) {
    getClientActivity(clientId: $clientId, cursor: $cursor, limit: $limit) {
      items {
        id
        at
        kind
        summary
        actorName
        refType
        refId
        isClientAction
        dateIsApprox
      }
      nextCursor
    }
  }
`;

export const GET_CLIENT_EMAILS = gql`
  query GetClientEmails($clientEmails: [String!]!) {
    getClientEmails(clientEmails: $clientEmails) {
      emails {
        attachments
        dateCreated
        emailRead
        emailSubject
        flag
        from
        id
        to
      }
      emailsSent {
        attachments
        dateCreated
        emailSubject
        from
        id
        to
      }
    }
  }
`;

export const GET_EMAIL_BY_ID = gql`
  query GetEmailById($emailId: String!, $getMore: Boolean!) {
    getEmailById(emailId: $emailId, getMore: $getMore) {
      emailChain {
        id
        emailText
        html
        emailSubject
        dateCreated
        from
        to
        attachments
        archive
        emailRead
        attachImgs {
          _id
          s3ImageUrl
          s3ThumbUrl
          beforeOrAfter
          dateEntered
          keapFileId
        }
        attachPdf {
          id
          s3FileUrl
          fileName
          dateEntered
        }
        flag
        chainedEmailIds
      }
      emailSentChain {
        id
        emailHTML
        emailSubject
        dateCreated
        from
        to
        attachments
        attachImgs {
          _id
          s3ImageUrl
          s3ThumbUrl
          beforeOrAfter
          dateEntered
          keapFileId
        }
        attachPdf {
          id
          s3FileUrl
          fileName
          dateEntered
        }
      }
      currentEmail {
        incEmail {
          id
          emailText
          html
          emailSubject
          dateCreated
          from
          to
          attachments
          archive
          emailRead
          attachImgs {
            _id
            s3ImageUrl
            s3ThumbUrl
            beforeOrAfter
            dateEntered
            keapFileId
          }
          attachPdf {
            id
            s3FileUrl
            fileName
            dateEntered
          }
          flag
          chainedEmailIds
        }
        sentEmail {
          id
          emailHTML
          emailSubject
          dateCreated
          from
          to
          attachments
          attachImgs {
            _id
            s3ImageUrl
            s3ThumbUrl
            beforeOrAfter
            dateEntered
            keapFileId
          }
          attachPdf {
            id
            s3FileUrl
            fileName
            dateEntered
          }
        }
      }
      moreInChain
      newerId
    }
  }
`;

export const GET_ALL_USERS = gql`
  query Query {
    getAllUsers {
      _id
      firstName
      lastName
      legalFirstName
      legalLastName
      email
      phoneNumberIds {
        _id
        phoneNumber
        numberType
        title
        recentTime
        textMessages {
          body
          sentReceivedTime
          outgoingBool
          twilioSID
          attachedImgs
        }
        unreadMessages
        hasTexts
      }
      permission
      initLogin
      failedLogins
      streetAddress
      city
      state
      zipCode
      refinReqInv {
        reqItemId
        reqItemCount
      }
      active
      hourlyWage
      defaultColor
      seniority
      payPercentage
    }
  }
`;

export const GET_VISITS = gql`
  query GetVisits($current: String!, $timeLimit: String!) {
    getVisits(current: $current, timeLimit: $timeLimit) {
      uniqueVisits
      home
      about
      contact
      ourDoors
      quote
      conversions
      signs
    }
  }
`;

export const GET_SIGN_ASSIGNMENT = gql`
  query GetSignAssign {
    getSignAssign {
      id
      signName
      signNum
    }
  }
`;

export const GET_RECENT_CLIENTS = gql`
  query GetRecentClients {
    getRecentClients {
      _id
      clientIds {
        _id
        firstName
        lastName
        clientStatus
      }
    }
  }
`;

export const GET_CLIENT_BY_ID = gql`
  query GetClientById($clientId: String!, $currentTime: String!, $activeTasksOnly: Boolean) {
    getClientById(clientId: $clientId, currentTime: $currentTime, activeTasksOnly: $activeTasksOnly) {
      _id
      firstName
      lastName
      spouseName
      phoneNumberIds {
        _id
        phoneNumber
        numberType
        title
        recentTime
        textConsent
        textMessages {
          _id
          body
          sentReceivedTime
          outgoingBool
          twilioSID
          attachedImgs
          status
          errorCode
          errorMessage
          statusUpdatedAt
        }
        unreadMessages
        hasTexts
      }
      primaryEmail
      secondaryEmail
      streetAddress
      city
      state
      zipCode
      zone
      gateCode
      clientNotes {
        _id
        note
        dateCreated
        dateUpdated
        updatedBy
        createdBy
      }
      clientStatus
      quotes {
        id
        quoteStatus
        quoteAccepted
        quoteType
        quoteViewed {
          timestamp
          isUser
        }
        dateCreated
        createdBy
        quoteItems {
          itemName
          price
          quantity
          discountApplicable
          jobType
          payBucket
          customPayCents
          requiresPhotos
        }
        quoteTotal
        subtotal
        customerName
        quoteNum
        expirationDate
        discountPercent
        quoteNote
        customerId
        splitBetween
        specialRefinisherPay {
          enabled
          amount
          payType
        }
      }
      invoices {
        id
        invoicePaid
        jobScheduledFor
        jobEndTime
        relatedTaskId
        dateCreated
        createdBy
        customerId
        invoiceNum
        quoteId
        invoiceNote
        paidBy
        invoiceItems {
          itemName
          price
          quantity
          discountApplicable
          jobType
          payBucket
          customPayCents
          requiresPhotos
        }
        invoiceTotal
        subtotal
        discountPercent
        customerName
        splitBetween
        specialRefinisherPay {
          enabled
          amount
          payType
        }
      }
      clientTasks {
        id
        assigneeName
        assigneeId
        taskType
        note
        associatedClientId
        associatedClientName
        active
        dateCreated
        dueDate
      }
      keapClientId
      keapClientMergeErrors
      clientPhotoIds
      referredBy {
        source
        sourceOptionId
      }
      managedById
      manager {
        _id
        firstName
        lastName
      }
      managedClientsCount
    }
  }
`;

export const GET_CLIENT_COMPLETED_TASKS = gql`
  query GetClientCompletedTasks($clientId: String!) {
    getClientCompletedTasks(clientId: $clientId) {
      id
      assigneeName
      assigneeId
      taskType
      note
      associatedClientId
      associatedClientName
      active
      dateCreated
      dueDate
    }
  }
`;

export const GET_BARE_SENT_EMAILS = gql`
  query GetSentEmails($page: String, $userEmailAddress: String, $showAll: Boolean) {
    getBareSentEmails(page: $page, userEmailAddress: $userEmailAddress, showAll: $showAll) {
      emails {
        id
        attachments
        dateCreated
        emailSubject
        from
        to
      }
      totalDoc
    }
  }
`;

// Sent search (3+ letters): recipient or subject anywhere, newest 200.
export const GET_BARE_SENT_EMAILS_SEARCH = gql`
  query GetSentEmailsSearch($searchTerm: String, $userEmailAddress: String, $showAll: Boolean) {
    getBareSentEmails(searchTerm: $searchTerm, userEmailAddress: $userEmailAddress, showAll: $showAll) {
      emails {
        id
        attachments
        dateCreated
        emailSubject
        from
        to
      }
      totalDoc
    }
  }
`;

export const GET_SENT_EMAIL_BY_ID = gql`
  query GetSentEmailById($sentId: String!) {
    getSentEmailById(sentId: $sentId) {
      id
      emailHTML
      emailSubject
      dateCreated
      from
      to
      attachments
      attachImgs {
        _id
        s3ImageUrl
        s3ThumbUrl
        beforeOrAfter
        dateEntered
      }
      attachPdf {
        id
        s3FileUrl
        fileName
        dateEntered
      }
    }
  }
`;

export const GET_UNSUBS = gql`
  query GetUnsubs {
    getUnsubs {
      id
      contact
    }
  }
`;

export const IS_UNSUB = gql`
  query Query($contact: String!) {
    isUnsub(contact: $contact)
  }
`;

export const GET_SERVICE_ITEMS = gql`
  query GetServicesItems {
    getServicesItems {
      id
      itemName
      discountApplicable
      price
      jobType
      payBucket
      customPayCents
      effectivePayBucket
      requiresPhotos
      order
      doorEquivalent
    }
  }
`;

export const GET_QUICK_TEXTS = gql`
  query GetQuickTexts($includeInactive: Boolean) {
    getQuickTexts(includeInactive: $includeInactive) {
      id
      label
      value
      order
      active
    }
  }
`;

export const GET_QUICK_TEXT_OPENER = gql`
  query GetQuickTextOpener {
    getQuickTextOpener
  }
`;

// Email templates Phase 1 (2026-09-16): the composer preview is rendered by
// the server with the same renderer that sends, so preview === sent email.
export const PREVIEW_EMAIL_TEMPLATE = gql`
  query PreviewEmailTemplate(
    $type: String!
    $text: String
    $quoteLink: [String]
    $invoiceLink: [String]
    $toFirstName: String
    $receivedPhoto: Boolean
    $from: String
  ) {
    previewEmailTemplate(
      type: $type
      text: $text
      quoteLink: $quoteLink
      invoiceLink: $invoiceLink
      toFirstName: $toFirstName
      receivedPhoto: $receivedPhoto
      from: $from
    ) {
      html
      warnings
    }
  }
`;

export const GET_EMAIL_TEMPLATE_DEFAULTS = gql`
  query GetEmailTemplateDefaults {
    getEmailTemplateDefaults {
      key
      label
      subjectDefault
      bodyDefault
    }
  }
`;

export const GET_EMAIL_SETTINGS = gql`
  query GetEmailSettings {
    getEmailSettings {
      maintProgramStartingCents
      maintProgramServicesItemId
      maintProgramServicesItemName
      maintProgramServicesItemPrice
    }
  }
`;

// Email templates Phase 2 (2026-09-17): templates as editable data.
export const EMAIL_TEMPLATE_FIELDS = `
      key
      name
      builtin
      active
      editable
      deactivatable
      composerPickable
      materialized
      order
      linkKind
      askReceivedPhoto
      usedBy
      subjectDefault
      bodyDefault
      topHtml
      bottomHtml
      updatedAtMs
      updatedByName
`;

export const GET_EMAIL_TEMPLATES = gql`
  query GetEmailTemplates($includeInactive: Boolean, $composerOnly: Boolean) {
    getEmailTemplates(includeInactive: $includeInactive, composerOnly: $composerOnly) {
      ${EMAIL_TEMPLATE_FIELDS}
    }
  }
`;

export const GET_EMAIL_TEMPLATE = gql`
  query GetEmailTemplate($key: String!) {
    getEmailTemplate(key: $key) {
      ${EMAIL_TEMPLATE_FIELDS}
      history {
        savedAtMs
        savedByName
        note
        snapshot {
          name
          subjectDefault
          bodyDefault
          topHtml
          bottomHtml
          linkKind
          askReceivedPhoto
          active
        }
      }
    }
  }
`;

export const GET_EMAIL_TEMPLATE_TOKENS = gql`
  query GetEmailTemplateTokens {
    getEmailTemplateTokens {
      name
      description
      cronOnly
      cronAllowed
    }
  }
`;

export const PREVIEW_EMAIL_TEMPLATE_DRAFT = gql`
  query PreviewEmailTemplateDraft($key: String, $draft: EmailTemplateInput!) {
    previewEmailTemplateDraft(key: $key, draft: $draft) {
      html
      warnings
      unresolvedTokens
    }
  }
`;

export const GET_BLOCKED_EMAILS = gql`
  query GetBlockedEmails {
    getBlockedEmails {
      id
      email
      emailsReceived
    }
  }
`;

export const GET_RECENT_CREATED_BAGS = gql`
  query GetRecentCreatedBAGs {
    getRecentCreatedBAGs {
      _id
      beforeId {
        _id
        s3ImageUrl
        s3ThumbUrl
        beforeOrAfter
        dateEntered
      }
      afterId {
        _id
        s3ImageUrl
        s3ThumbUrl
        beforeOrAfter
        dateEntered
      }
      weekGalleryDisplay
      dateEntered
      refinishers
      stainColor
    }
  }
`;

// Image Groups > Gallery Pairs (admin only). s3ImageUrl is selected for the
// DOWNLOAD only — the grid and the modal render s3ThumbUrl.
export const GET_GALLERY_PAIRS = gql`
  query GetGalleryPairs($page: Int!) {
    getGalleryPairs(page: $page) {
      totalCount
      page
      pageSize
      groups {
        _id
        beforeId {
          _id
          s3ImageUrl
          s3ThumbUrl
        }
        afterId {
          _id
          s3ImageUrl
          s3ThumbUrl
        }
        dateEntered
        refinishers
        stainColor
      }
    }
  }
`;

export const GET_QUOTE_COUNT = gql`
  query getQuoteCount {
    getQuoteCount
  }
`;

export const GET_OUTWARD_CLIENT_QUOTE = gql`
  query getCustomerOutwardQuote(
    $quoteId: String!
    $localEpoch: String!
    $isUser: Boolean!
    $accessToken: String
  ) {
    getCustomerOutwardQuote(
      quoteId: $quoteId
      localEpoch: $localEpoch
      isUser: $isUser
      accessToken: $accessToken
    ) {
      dateCreated
      quoteItems {
        itemName
        price
        quantity
        discountApplicable
      }
      quoteTotal
      subtotal
      customerName
      quoteNum
      expirationDate
      discountPercent
      quoteNote
      quoteAccepted
      quoteStatus
    }
  }
`;

export const CLIENT_SEARCH = gql`
  query SearchClients(
    $searchParam: String
    $forManagerOfClientId: String
  ) {
    searchClients(
      searchParam: $searchParam
      forManagerOfClientId: $forManagerOfClientId
    ) {
      _id
      firstName
      lastName
      primaryEmail
      phoneNumberIds {
        _id
        phoneNumber
        numberType
      }
      manager {
        _id
        firstName
        lastName
      }
    }
  }
`;

// Manager Linking — lazy-fetched when the ManagesListModal is opened on a
// manager's single client page. Kept light (id + names only); the modal's
// "view" link opens the full client page in a new tab for full details.
export const GET_MANAGED_CLIENTS = gql`
  query GetManagedClients($managerId: String!) {
    getManagedClients(managerId: $managerId) {
      _id
      firstName
      lastName
    }
  }
`;

export const GET_QUOTE_HISTORY = gql`
  query GetCustomerQuoteHistory($quoteId: String!) {
    getCustomerQuoteHistory(quoteId: $quoteId) {
      timestamp
      isUser
    }
  }
`;

export const GET_USER_WEB_AUTHN_IDS = gql`
  query Query {
    getUserWebAuthnIds
  }
`;

export const GET_USER_INFO_FOR_TASKS = gql`
  query GetUsersNamesForTasks {
    getUsersNamesForTasks {
      _id
      firstName
      lastName
    }
  }
`;

export const GET_TASK_BY_ID = gql`
  query GetTaskById($taskId: String!) {
    getTaskById(taskId: $taskId) {
      id
      assigneeName
      assigneeId
      taskType
      note
      associatedClientId
      associatedClientName
      active
      dateCreated
      dueDate
    }
  }
`;

export const GET_TASK_FOR_USERS = gql`
  query GetTasksForUser($sort: String, $time: String) {
    getTasksForUser(sort: $sort, time: $time) {
      id
      assigneeName
      assigneeId
      taskType
      note
      associatedClientId
      associatedClientName
      active
      dateCreated
      dueDate
    }
  }
`;

export const GET_OUTWARD_CLIENT_INVOICE = gql`
  query GetClientOutwardInvoice($invoiceId: String!, $accessToken: String) {
    getClientOutwardInvoice(invoiceId: $invoiceId, accessToken: $accessToken) {
      id
      jobScheduledFor
      invoiceNote
      invoiceNum
      invoiceItems {
        itemName
        price
        quantity
        discountApplicable
      }
      invoiceTotal
      subtotal
      discountPercent
      customerName
      invoicePaid
    }
  }
`;

export const GET_PHONE_INFO_FOR_TEXTS = gql`
  query getPhonesInfoForText($useUnreadSearch: Boolean) {
    getPhonesInfoForText(useUnreadSearch: $useUnreadSearch) {
      _id
      phoneNumber
      title
      unreadMessages
      recentTime
      textConsent
      owners {
        ownerType
        ownerId
        firstName
        lastName
      }
    }
  }
`;

// Phone-uniqueness (2026-09-04): ALL owners (clients + users) of one phone
// doc — MessagesPage owner list + the assign menu's current-owner display.
export const GET_CLIENTS_FOR_PHONE = gql`
  query GetClientsForPhone($phnId: String!) {
    getClientsForPhone(phnId: $phnId) {
      ownerType
      ownerId
      firstName
      lastName
    }
  }
`;

// T3.1 windowed thread load (2026-09-08): limit returns only the LAST
// `limit` messages; beforeMs (with limit) returns the last `limit` messages
// STRICTLY older than beforeMs — the load-older page. Both omitted = full
// thread (legacy). "Has more" is inferred when a window comes back full.
export const GET_PHONE_TEXTS_BY_ID = gql`
  query GetPhoneNumTextsById(
    $phoneId: String!
    $localEpoch: String!
    $dontUpdate: Boolean
    $limit: Int
    $beforeMs: String
  ) {
    getPhoneNumTextsById(
      phoneId: $phoneId
      localEpoch: $localEpoch
      dontUpdate: $dontUpdate
      limit: $limit
      beforeMs: $beforeMs
    ) {
      _id
      phoneNumber
      numberType
      title
      recentTime
      textConsent
      textMessages {
        _id
        body
        sentReceivedTime
        outgoingBool
        twilioSID
        attachedImgs
        status
        errorCode
        errorMessage
        statusUpdatedAt
      }
      unreadMessages
      hasTexts
    }
  }
`;

// Incremental fetch — returns only messages newer than sinceEpoch for one
// phone number (used by the SSE focused-thread live-append path). Returns a
// partial type (NOT PhoneNumber) carrying just textMessages + updated
// recentTime. Server marks-read scoped to the messages actually returned.
export const GET_PHONE_TEXTS_SINCE = gql`
  query GetPhoneNumTextsSince(
    $phoneId: String!
    $sinceEpoch: String!
    $localEpoch: String!
  ) {
    getPhoneNumTextsSince(
      phoneId: $phoneId
      sinceEpoch: $sinceEpoch
      localEpoch: $localEpoch
    ) {
      _id
      recentTime
      textMessages {
        _id
        body
        sentReceivedTime
        outgoingBool
        twilioSID
        attachedImgs
        status
        errorCode
        errorMessage
        statusUpdatedAt
      }
    }
  }
`;

export const SINGLE_CLIENT_MSGS_CHECK = gql`
  query Query($phnIdArr: [String]) {
    checkSingleClientNewMsgs(phnIdArr: $phnIdArr)
  }
`;

export const GET_STRIPE_TRANSACTIONS = gql`
  query GetStripeTransactions {
    getStripeTransactions {
      id
      stripePIId
      amount
      tip
      dateCreated
      invoiceId
      invoiceNum
      invoiceTotal
      reason
    }
  }
`;

export const GET_CLIENT_INVOICE_BY_NUM = gql`
  query GetClientInvoiceByNum($invoiceNum: String!) {
    getClientInvoiceByNum(invoiceNum: $invoiceNum) {
      id
      invoicePaid
      invoiceNum
      customerName
      invoiceTotal
      paidBy
      invoiceItems {
        itemName
      }
    }
  }
`;

export const GET_KEY_SHORTCUTS = gql`
  query GetKeyShortcuts {
    getKeyShortcuts {
      id
      title
      text
    }
  }
`;

export const GET_WEBSITE_VERSION = gql`
  query GetWebsiteVersion {
    getWebsiteVersion {
      version
    }
  }
`;

// Returns the user's currently-open shift (clockOutMs == null), or null if
// they aren't clocked in. Drives the "Clocked In · Xh Ym · Clock Out" state
// of the dashboard widget.
export const GET_OPEN_SHIFT = gql`
  query GetOpenShift($userId: String!) {
    getOpenShift(userId: $userId) {
      id
      userId
      clockInMs
      clockOutMs
      source
      createdByUserId
      editedAt
      editedByUserId
    }
  }
`;

// Returns every TimeEntry whose clockInMs falls inside [periodStartMs,
// periodEndMs]. Pay-period range is Thursday 00:00 → Wednesday 23:59 by
// convention; caller computes the bounds via getPayPeriodBounds equivalent.
export const GET_USER_TIME_ENTRIES = gql`
  query GetUserTimeEntries(
    $userId: String!
    $periodStartMs: String!
    $periodEndMs: String!
  ) {
    getUserTimeEntries(
      userId: $userId
      periodStartMs: $periodStartMs
      periodEndMs: $periodEndMs
    ) {
      id
      userId
      clockInMs
      clockOutMs
      source
      createdByUserId
      editedAt
      editedByUserId
    }
  }
`;

export const GET_MAINT_REM_PAGE_INFO = gql`
  query GetMaintenanceReminderPageInfo($currentDate: String) {
    getMaintenanceReminderPageInfo(currentDate: $currentDate) {
      upcoming
      currentReminders {
        id
        assigneeName
        assigneeId
        taskType
        note
        associatedClientId
        associatedClientName
        active
        dateCreated
        dueDate
      }
      history {
        dateCreated
        text
      }
    }
  }
`;

export const GET_CYCLE_CONFIRMATION_CANDIDATES = gql`
  query GetCycleConfirmationCandidates(
    $currentDate: String!
    $withinDays: Int
  ) {
    getCycleConfirmationCandidates(
      currentDate: $currentDate
      withinDays: $withinDays
    ) {
      windowDays
      futureCount
      dueSoon {
        task {
          id
          assigneeName
          assigneeId
          taskType
          note
          associatedClientId
          associatedClientName
          active
          dateCreated
          dueDate
          cyclePositionAtCreate
        }
        invoiceId
        invoiceNum
        invoiceJobScheduledFor
      }
    }
  }
`;

export const GET_MAINT_CYCLE_INFERENCE_FOR_TASK = gql`
  query GetMaintenanceCycleInferenceForTask($taskId: ID!) {
    getMaintenanceCycleInferenceForTask(taskId: $taskId) {
      taskId
      invoiceId
      invoiceNum
      associatedClientId
      associatedClientName
      suggestedPosition
      confidence
      reason
      totalR
      chainLength
      candidateInvoices {
        invoiceId
        invoiceNum
        jobScheduledFor
        derivedJobType
        isCurrent
        isAnchor
      }
      taskType
      assigneeId
      taskActive
      currentCyclePosition
    }
  }
`;

export const GET_SCHEDULE_RANGE = gql`
  query GetScheduleRange($rangeStart: String!, $rangeEnd: String!, $onlyGetInvoiced: Boolean, $includePersonal: Boolean) {
    getScheduleRange(rangeStart: $rangeStart, rangeEnd: $rangeEnd, onlyGetInvoiced: $onlyGetInvoiced, includePersonal: $includePersonal) {
      calendarItem {
        id
        label
        isAllDay
        invoiceId
        quoteId
        clientId
        startMs
        endMs
        workDays {
          startMs
          endMs
        }
        priorityNotes
        assignedUserIds
        itemStatus
        locked
        isPlaceholder
        # Placeholder Min/Max (2026-10-29): a tentative placeholder's own
        # refinisher range (no doc to hold it). Read by the block chip and the
        # Edit modal ONLY while isPlaceholder is true; null on every other item.
        placeholderSplitBetween
        # Available Sooner (2026-10-07). Selected HERE so the event detail
        # popup can show and toggle the current state; the popup is fed by
        # this query on every calendar surface.
        availableSooner
        eventType
        chainGroupId
        note
        photoIds
        specialRefinisherPay {
          enabled
          amount
          payType
        }
        # Recurring personal events (2026-09-20). An occurrence carries a
        # COMPOSITE id "<parentId>:<occurrenceDayMs>" and is not a document —
        # prefer seriesId / occurrenceDayMs over parsing that id.
        # recurrenceText is composed server-side so the wording cannot drift
        # between this app and the phone; recurrenceInvalid marks a rule that
        # can no longer produce occurrences (rendered as a one-off on its
        # anchor day so it stays reachable); splitAtMs is set on the half a
        # "this and following" edit created, truncatedAtMs (2026-09-25) on the
        # half it was created FROM.
        seriesId
        occurrenceDayMs
        recurrenceText
        recurrenceInvalid
        splitAtMs
        truncatedAtMs
        occurrenceOverridden
        recurrence {
          freq
          interval
          byWeekday
          byMonthDay
          untilMs
          skippedDayMs
          overrides {
            dayMs
            label
            note
            isAllDay
            startMs
            endMs
          }
        }
      }
      customer {
        _id
        firstName
        lastName
        streetAddress
        city
        state
        zipCode
        phoneNumberIds {
          phoneNumber
          numberType
        }
        clientPhotoIds
      }
      invoice {
        id
        invoiceNum
        invoiceTotal
        invoicePaid
        paidBy
        invoiceNote
        invoiceItems {
          itemName
          price
          quantity
          discountApplicable
          jobType
          payBucket
          customPayCents
          requiresPhotos
        }
        subtotal
        discountPercent
        customerName
        dateCreated
        createdBy
        customerId
        jobScheduledFor
        jobEndTime
        quoteId
        splitBetween
        specialRefinisherPay {
          enabled
          amount
          payType
        }
      }
      quote {
        id
        quoteNum
        quoteTotal
        quoteStatus
        quoteItems {
          itemName
          price
          quantity
          discountApplicable
          jobType
          payBucket
          customPayCents
          requiresPhotos
        }
        subtotal
        discountPercent
        customerName
        dateCreated
        createdBy
        customerId
        quoteNote
        splitBetween
        specialRefinisherPay {
          enabled
          amount
          payType
        }
      }
      assignedUsers {
        _id
        firstName
        lastName
        defaultColor
      }
      calNotes
      zone
    }
  }
`;

// Per-zone weather over [startMs, endMs] (weather-by-zone, 2026-06-22). Fired
// in parallel with GET_SCHEDULE_RANGE; the client joins each job to its day via
// item.zone + the Central (America/Chicago) calendar date of its startMs. All
// values are Strings; precipProb/windMph may be null = "no data" ("--"). This
// query is intentionally DECOUPLED from the calendar_update SSE refetch in the
// week view (weather doesn't change when a job moves) and cached ~45m per week.
export const GET_ZONE_WEATHER_FOR_RANGE = gql`
  query GetZoneWeatherForRange($startMs: String!, $endMs: String!) {
    getZoneWeatherForRange(startMs: $startMs, endMs: $endMs) {
      zone
      fetchedMs
      days {
        localDate
        tempHi
        tempLo
        precipProb
        windMph
        condition
        icon
      }
    }
  }
`;

// Tentative placeholders for one client — feeds the cleanup modal. Server
// returns ONLY true placeholders (job, isPlaceholder:true); invoice/quote are
// always null here. Lean selection: just what the checklist row needs.
export const GET_CLIENT_PLACEHOLDERS = gql`
  query GetClientPlaceholders($clientId: String!) {
    getClientPlaceholders(clientId: $clientId) {
      calendarItem {
        id
        startMs
        endMs
        assignedUserIds
        priorityNotes
        note
        isPlaceholder
        # Placeholder Min/Max (2026-10-29) — shown on each cleanup row.
        placeholderSplitBetween
        eventType
      }
      customer {
        _id
        firstName
        lastName
      }
      assignedUsers {
        _id
        firstName
        lastName
        defaultColor
      }
    }
  }
`;

// Pre-check for "promote in place": the existing job event already on the
// calendar for an invoice/quote (or null). Drives the confirmation warning.
export const GET_CALENDAR_EVENT_FOR_DOC = gql`
  query GetCalendarEventForDoc($invoiceId: String, $quoteId: String) {
    getCalendarEventForDoc(invoiceId: $invoiceId, quoteId: $quoteId) {
      calendarItem {
        id
        startMs
        endMs
        invoiceId
        quoteId
        isPlaceholder
        eventType
        assignedUserIds
        specialRefinisherPay {
          enabled
          amount
          payType
        }
      }
      customer {
        _id
        firstName
        lastName
      }
      invoice {
        id
        invoiceNum
      }
      quote {
        id
        quoteNum
      }
      assignedUsers {
        _id
        firstName
        lastName
      }
    }
  }
`;

export const GET_SCHEDULE_MONTH_SUMMARY = gql`
  query GetScheduleMonthSummary($monthStart: String!, $monthEnd: String!, $onlyGetInvoiced: Boolean, $includePersonal: Boolean) {
    getScheduleMonthSummary(monthStart: $monthStart, monthEnd: $monthEnd, onlyGetInvoiced: $onlyGetInvoiced, includePersonal: $includePersonal) {
      dateMs
      count
      userColors
      hasPriorityNotes
      personalUserColors
      personalCount
    }
  }
`;

export const GET_JOB_FORM_BY_INVOICE_ID = gql`
  query GetJobFormByInvoiceId($invoiceId: String!) {
    getJobFormByInvoiceId(invoiceId: $invoiceId) {
      _id
      invoiceId
      invoiceNum
      calendarItemId
      clientId
      stainOptionId {
        _id
        title
        listType
        popularityGrade
      }
      stainIsOther
      customStainText
      hardwareOptionId {
        _id
        title
        listType
        popularityGrade
      }
      hardwareIsOther
      customHardwareText
      dryDoorOptionId {
        _id
        title
        listType
        popularityGrade
      }
      dryDoorIsOther
      customDryDoorText
      thresholdIsAdjustable
      adjustedThreshold
      replacedDoorSweep
      changedWeatherStrip
      foamWedges
      installedNewHardware
      handleOperationId {
        _id
        optionName
      }
      deadboltOperationId {
        _id
        optionName
      }
      selectedRefinisherUserIds
      customRefinisherNames
      selectedRefinisherUsers {
        _id
        firstName
        lastName
        defaultColor
      }
      bagroupIds {
        _id
        weekGalleryDisplay
        beforeId {
          _id
          s3ImageUrl
          s3ThumbUrl
          beforeOrAfter
          dateEntered
        }
        afterId {
          _id
          s3ImageUrl
          s3ThumbUrl
          beforeOrAfter
          dateEntered
        }
        jobFormId
        clientIdempotencyKey
      }
      additionalPhotos {
        _id
        s3ImageUrl
        s3ThumbUrl
        beforeOrAfter
        dateEntered
      }
      customerSatisfaction
      amountReceived
      paymentMethod
      jobType
      notes
      status
      createdBy
      dateCreated
      lastUpdatedBy
      dateUpdated
      submittedBy
      dateSubmitted
      reviewedBy
      dateReviewed
      version
      activeViewers {
        userId
        userName
        userColor
        lastHeartbeat
      }
    }
  }
`;

export const GET_CLIENT_INVOICE_BY_ID = gql`
  query GetClientInvoiceById($invoiceId: String!) {
    getClientInvoiceById(invoiceId: $invoiceId) {
      id
      invoicePaid
      paidBy
      invoiceNum
      invoiceItems {
        itemName
        price
        quantity
        discountApplicable
        jobType
        payBucket
        customPayCents
        requiresPhotos
      }
      invoiceTotal
      subtotal
      discountPercent
      customerName
      splitBetween
      specialRefinisherPay {
        enabled
        amount
        payType
      }
      invoiceNote
      jobScheduledFor
      jobEndTime
      dateCreated
      createdBy
      customerId
      quoteId
      relatedTaskId
    }
  }
`;

export const GET_ALL_JOB_OPTIONS_LIST = gql`
  query GetAllJobOptionsList {
    getAllJobOptionsList {
      _id
      title
      listType
      popularityGrade
    }
  }
`;

export const GET_JOB_FORMS_FOR_REVIEW = gql`
  query GetJobFormsForReview($status: String, $sinceDate: String) {
    getJobFormsForReview(status: $status, sinceDate: $sinceDate) {
      _id
      invoiceId
      invoiceNum
      customerName
      calendarItemId
      clientId
      stainOptionId {
        _id
        title
        listType
        popularityGrade
      }
      stainIsOther
      customStainText
      hardwareOptionId {
        _id
        title
        listType
        popularityGrade
      }
      hardwareIsOther
      customHardwareText
      dryDoorOptionId {
        _id
        title
        listType
        popularityGrade
      }
      dryDoorIsOther
      customDryDoorText
      thresholdIsAdjustable
      adjustedThreshold
      replacedDoorSweep
      changedWeatherStrip
      foamWedges
      installedNewHardware
      handleOperationId {
        _id
        optionName
      }
      deadboltOperationId {
        _id
        optionName
      }
      selectedRefinisherUserIds
      customRefinisherNames
      selectedRefinisherUsers {
        _id
        firstName
        lastName
        defaultColor
      }
      customerSatisfaction
      amountReceived
      paymentMethod
      jobType
      notes
      status
      createdBy
      dateCreated
      submittedBy
      dateSubmitted
      reviewedBy
      dateReviewed
      dateUpdated
    }
  }
`;

export const GET_REFINISHER_USERS = gql`
  query GetRefinisherUsers {
    getRefinisherUsers {
      _id
      firstName
      lastName
      defaultColor
    }
  }
`;

export const GET_JOB_OPTIONS_LIST = gql`
  query GetJobOptionsList($listType: String!) {
    getJobOptionsList(listType: $listType) {
      _id
      title
      listType
      popularityGrade
    }
  }
`;

export const GET_ALL_REFERRED_BY_OPTIONS = gql`
  query GetAllReferredByOptions {
    getAllReferredByOptions {
      _id
      source
      costHistory {
        monthlyCents
        effectiveFrom
        setBy
        setAt
      }
    }
  }
`;

// Referral system — admin Defaults Setup → Referral Fee tab. Server uses
// defensive upsert so the doc always exists with at least zero fee.
export const GET_REFERRAL_SETTINGS = gql`
  query GetReferralSettings {
    getReferralSettings {
      _id
      referralFee {
        type
        valueCents
      }
      updatedAtMs
      updatedByUserId
    }
  }
`;

// Public (no auth) — the manually-maintained Google review count + rating +
// reviews link shown on the website. Singleton; read with cache-first so it's
// fetched once and shared across every page via the normalized cache.
export const GET_REVIEW_COUNTER = gql`
  query GetReviewCounter {
    getReviewCounter {
      _id
      reviewCount
      rating
      url
      updatedAtMs
    }
  }
`;

// Referral system — list referral submissions for the admin Referrals page.
// `filter.completed` scopes to Open vs Completed tab; `filter.limit` caps
// the Completed tab to most-recent N by submittedAtMs.
export const GET_REFERRAL_SUBMISSIONS = gql`
  query GetReferralSubmissions($filter: ReferralSubmissionFilter) {
    getReferralSubmissions(filter: $filter) {
      _id
      submitterUserId
      firstName
      lastName
      primaryEmail
      phoneNumber
      phoneType
      streetAddress
      city
      state
      zipCode
      textConsent
      source
      sourceOptionId
      otherSourceText
      discount
      notes
      referredByUserId
      photoId
      submittedAtMs
      completed
      linkedClientId
      completedAtMs
      completedByUserId
    }
  }
`;

export const GET_PENDING_REFERRAL_COUNT = gql`
  query GetPendingReferralCount {
    getPendingReferralCount
  }
`;

// Duplicate-check before creating a customer from a referral. Phone +
// email matches BLOCK; address matches WARN. Server normalizes both
// sides at compare time.
export const CHECK_CLIENT_DUPLICATES = gql`
  query CheckClientDuplicates(
    $email: String
    $phone: String
    $streetAddress: String
    $city: String
    $state: String
    $zipCode: String
  ) {
    checkClientDuplicates(
      email: $email
      phone: $phone
      streetAddress: $streetAddress
      city: $city
      state: $state
      zipCode: $zipCode
    ) {
      emailMatches { _id firstName lastName primaryEmail phoneNumber streetAddress city state zipCode }
      phoneMatches { _id firstName lastName primaryEmail phoneNumber streetAddress city state zipCode }
      addressMatches { _id firstName lastName primaryEmail phoneNumber streetAddress city state zipCode }
    }
  }
`;

// JFR referredByUser dropdown + Referrals create-customer flow. Server
// excludes self and inactive users.
export const GET_ACTIVE_USERS_FOR_REFERRAL = gql`
  query GetActiveUsersForReferral {
    getActiveUsersForReferral {
      _id
      firstName
      lastName
    }
  }
`;

// Push 1 build 146 — used by JobFormReviewModal source-dropdown to
// fetch the client's current referredBy without round-tripping the
// full Client doc.
export const GET_CLIENT_REFERRED_BY = gql`
  query GetClientReferredBy($clientId: String!) {
    getClientReferredBy(clientId: $clientId) {
      source
      sourceOptionId
      userId
      setAtMs
      setByUserId
    }
  }
`;

// Lightweight notes-only fetch for the CustomerNotesHelperButton —
// used next to referral-source dropdowns so the office can scan
// legacy notes for "ref / referred / referred by" mentions without
// loading the full Client doc.
export const GET_CLIENT_NOTES_BY_ID = gql`
  query GetClientNotesById($clientId: String!) {
    getClientNotesById(clientId: $clientId) {
      _id
      note
      dateCreated
      dateUpdated
      updatedBy
      createdBy
    }
  }
`;

// Push 1 build 146 — list-row patch on job_form_update SSE without
// fetching the heavy JobForm payload (no photos, no BAGroups).
export const GET_JOB_FORM_REVIEW_SUMMARY = gql`
  query GetJobFormReviewSummary($invoiceId: String!) {
    getJobFormReviewSummary(invoiceId: $invoiceId) {
      _id
      invoiceId
      invoiceNum
      customerName
      status
      paymentMethod
      amountReceived
      customerSatisfaction
      reviewedBy
      dateReviewed
      dateUpdated
      submittedBy
      dateSubmitted
    }
  }
`;

export const GET_ALL_HARDWARE_OPERATION_OPTIONS = gql`
  query GetAllHardwareOperationOptions {
    getAllHardwareOperationOptions {
      _id
      optionName
    }
  }
`;

// Developer-only. Used by the JFR page dev photo-picker to attach an orphaned
// photo to a BAGroup. Server gates this to role=developer; a non-dev hitting
// it gets "Forbidden".
//
// startDate/endDate (optional, 2026-08-26) are INCLUSIVE epoch-ms strings. Omit
// both for the newest uploads; send both to window-search on `dateEntered`
// instead (newest first). Server caps `limit` at 300 either way.
export const GET_RECENT_CLIENT_DOOR_PHOTOS = gql`
  query GetRecentClientDoorPhotos($limit: Int!, $startDate: String, $endDate: String) {
    getRecentClientDoorPhotos(limit: $limit, startDate: $startDate, endDate: $endDate) {
      _id
      s3ImageUrl
      s3ThumbUrl
      beforeOrAfter
      dateEntered
      note
    }
  }
`;

export const GET_PENDING_JOB_FORM_COUNT = gql`
  query GetPendingJobFormCount {
    getPendingJobFormCount
  }
`;

export const GET_STUCK_SUBMISSIONS = gql`
  query GetStuckSubmissions($resolved: Boolean) {
    getStuckSubmissions(resolved: $resolved) {
      _id
      invoiceId
      invoiceNum
      customerName
      jobFormId
      refinisherUserId {
        _id
        firstName
        lastName
      }
      firstSubmitAttemptAt
      lastSyncAttemptAt
      backoffStage
      photoMissingLocalIds
      partialUploadPhotoServerIds
      partialUploadBAGroupIds
      lastMobileErrorMessage
      mobileAppBuild
      escalated
      resolvedAt
      resolvedBy {
        _id
        firstName
        lastName
      }
      resolutionKind
      resolutionNote
      createdAt
      updatedAt
    }
  }
`;

export const GET_STUCK_SUBMISSION_COUNT = gql`
  query GetStuckSubmissionCount {
    getStuckSubmissionCount
  }
`;

export const GET_LEDGER_ITEMS = gql`
  query GetLedgerItems($startDate: String!, $endDate: String!) {
    getLedgerItems(startDate: $startDate, endDate: $endDate) {
      _id
      refinishers {
        assignedId
        name
      }
      client {
        clientId
        clientName
      }
      scheduleDate
      zipCode
      amount
      referredBy {
        source
        sourceOptionId
      }
      jobFormId
      jobType
      dateCreated
    }
  }
`;

export const GET_OLDEST_LEDGER_YEAR = gql`
  query GetOldestLedgerYear {
    getOldestLedgerYear
  }
`;

// The Business Ledger's search across EVERY year
// (plans/plan_business_ledger_search_tours_2026_10_23.md). Only what the rows
// render plus the two result scalars. `truncated` is the ONE truncation signal
// the page reads — never computed client-side. Fetched with fetchPolicy
// "no-cache" (BusinessLedgerPage) so these narrower rows never overwrite the
// year cache's normalised LedgerItem objects.
export const SEARCH_LEDGER_ITEMS = gql`
  query SearchLedgerItems($term: String!) {
    searchLedgerItems(term: $term) {
      items {
        _id
        refinishers {
          name
        }
        client {
          clientName
        }
        scheduleDate
        zipCode
        amount
        referredBy {
          source
        }
        jobType
      }
      totalDoc
      truncated
    }
  }
`;

// Admin/developer only — enforced server-side in the resolver, not just by the
// route gate. All *Cents fields are STRINGS of integer cents.
export const GET_BUSINESS_METRICS = gql`
  query GetBusinessMetrics($year: Int, $allYears: Boolean) {
    getBusinessMetrics(year: $year, allYears: $allYears) {
      byYear {
        year
        jobs
        revenueCents
        avgJobValueCents
        mJobs
        mRevenueCents
        mAvgJobValueCents
        rJobs
        rRevenueCents
        rAvgJobValueCents
        otherJobs
        otherRevenueCents
        otherAvgJobValueCents
        attributedJobs
        newClients
        repeatClients
      }
      byMonth {
        month
        jobs
        revenueCents
        avgJobValueCents
        pctOfPeriod
        priorYearRevenueCents
        priorYearJobs
        partial
      }
      monthlySeries {
        year
        month
        jobs
        revenueCents
      }
      comparisons {
        monthOverMonth { ...bmCompare }
        monthVsSameMonthLastYear { ...bmCompare }
        quarterOverQuarter { ...bmCompare }
        quarterVsSameQuarterLastYear { ...bmCompare }
        ytdVsSamePeriodLastYear { ...bmCompare }
        ttmVsPriorTtm { ...bmCompare }
      }
      seasonality {
        month
        index
      }
      crew {
        name
        jobs
        revenueCents
        avgJobValueCents
      }
      clients {
        newCount
        repeatCount
        repeatRatePct
        concentrationTop10Pct
        topClients {
          name
          jobs
          revenueCents
        }
      }
      leadSources {
        sourceOptionId
        label
        jobs
        revenueCents
        avgJobValueCents
      }
      unattributedCount
      zips {
        zip
        jobs
        revenueCents
        firstYear
      }
      projection {
        projectedYearEndCents
        actualYtdCents
        projectedRemainderCents
        monthsElapsed
        elapsedFractionOfCurrentMonth
        runRateCents
        method
        basisLabel
        lowConfidence
      }
      cagr {
        value
        firstYear
        lastCompleteYear
      }
      meta {
        unparseableCount
        sampleYears
        generatedAtMs
        year
        allYears
        displayYear
        firstFullYear
      }
    }
  }

  fragment bmCompare on BMComparison {
    currentCents
    currentJobs
    currentAvgJobValueCents
    priorCents
    priorJobs
    priorAvgJobValueCents
    spanDays
    clamped
  }
`;

export const GET_ADMIN_PAYROLL_REPORT = gql`
  query GetAdminPayrollReport($startMs: String!, $endMs: String!) {
    getAdminPayrollReport(startMs: $startMs, endMs: $endMs) {
      periodStart
      periodEnd
      users {
        userId
        firstName
        lastName
        defaultColor
        payPercentage
        confirmedTotal
        confirmedRefinishTotal
        confirmedMaintTotal
        confirmedTipTotal
        confirmedHcTotal
        confirmedTripChargeTotal
        confirmedOtherTotal
        jobCount
        entries {
          calendarItemId
          invoiceId
          invoiceNum
          customerName
          scheduleDate
          refinishPay
          maintPay
          tipPay
          hcPay
          tripChargePay
          otherPay
          totalPay
          numAssigned
        }
        referralEntries {
          payoutId
          clientId
          customerName
          triggeringJobFormId
          amountCents
          earnedAtMs
        }
        referralFeeTotal
        advanceEntries {
          advanceId
          userId
          amountCents
          periodEndMs
          createdByUserId
          createdAt
          updatedAt
        }
        advanceTotal
        netTotal
      }
      hourlyUsers {
        userId
        firstName
        lastName
        defaultColor
        hourlyWageCents
        totalMs
        totalHours
        payCents
        entryCount
        entries {
          entryId
          clockInMs
          clockOutMs
          source
        }
        advanceEntries {
          advanceId
          userId
          amountCents
          periodEndMs
          createdByUserId
          createdAt
          updatedAt
        }
        advanceTotal
        netTotal
      }
    }
  }
`;

// ── Payroll Snapshot History (2026-06-09) ──────────────────────────────
// Shared selection for an AdminPayrollResult (the snapshot's frozen
// issuedReport and the live recompute both use this exact shape). Interpolated
// into the snapshot queries below so the field set can't drift between them.
const PAYROLL_RESULT_BODY = `
  periodStart
  periodEnd
  users {
    userId firstName lastName defaultColor payPercentage
    confirmedTotal confirmedRefinishTotal confirmedMaintTotal confirmedTipTotal
    confirmedHcTotal confirmedTripChargeTotal confirmedOtherTotal jobCount
    entries {
      calendarItemId invoiceId invoiceNum clientId customerName scheduleDate
      refinishPay maintPay tipPay hcPay tripChargePay otherPay totalPay numAssigned
    }
    referralEntries { payoutId clientId customerName triggeringJobFormId amountCents earnedAtMs }
    referralFeeTotal
    advanceEntries { advanceId userId amountCents periodEndMs createdByUserId createdAt updatedAt }
    advanceTotal netTotal
  }
  hourlyUsers {
    userId firstName lastName defaultColor hourlyWageCents totalMs totalHours payCents entryCount
    entries { entryId clockInMs clockOutMs source }
    advanceEntries { advanceId userId amountCents periodEndMs createdByUserId createdAt updatedAt }
    advanceTotal netTotal
  }
`;

export const GET_PAYROLL_SNAPSHOT = gql`
  query GetPayrollSnapshot($periodEndMs: String!) {
    getPayrollSnapshot(periodEndMs: $periodEndMs) {
      id periodStartMs periodEndMs issuedByUserId issuedAtMs source unreviewed
      reconciliations { reconciledAtMs reconciledByUserId reason diffJson }
      issuedReport { ${PAYROLL_RESULT_BODY} }
      currentReport { ${PAYROLL_RESULT_BODY} }
      reconciledVersions {
        createdAtMs createdByUserId reason
        report { ${PAYROLL_RESULT_BODY} }
      }
    }
  }
`;

export const LIST_PAYROLL_SNAPSHOTS = gql`
  query ListPayrollSnapshots {
    listPayrollSnapshots {
      id periodStartMs periodEndMs issuedByUserId issuedAtMs source unreviewed
      reconciliationCount grandNetTotal refinisherCount hourlyCount
    }
  }
`;

// Hourly ("Office") paystub data (2026-07-31, admin-only) — web mirror of
// the emailed hourly statement; same server builders as the email pipeline.
export const GET_HOURLY_PAYSTUB_DATA = gql`
  query GetHourlyPaystubData($userId: String!, $periodEndMs: String!) {
    getHourlyPaystubData(userId: $userId, periodEndMs: $periodEndMs) {
      userId
      firstName
      lastName
      employeePhone
      employeeAddressLine1
      employeeAddressLine2
      periodStartMs
      periodEndMs
      payDateMs
      periodSource
      hourlyWageCents
      totalMs
      payCents
      ytdPayCents
      entries {
        clockInMs
        clockOutMs
      }
    }
  }
`;

// Batch hourly stubs — every pay period touched by the range, zero-pay
// periods omitted server-side (mirrors GET_PAYSTUB_DATA_RANGE).
export const GET_HOURLY_PAYSTUB_DATA_RANGE = gql`
  query GetHourlyPaystubDataRange($userId: String!, $startMs: String!, $endMs: String!) {
    getHourlyPaystubDataRange(userId: $userId, startMs: $startMs, endMs: $endMs) {
      userId
      firstName
      lastName
      employeePhone
      employeeAddressLine1
      employeeAddressLine2
      periodStartMs
      periodEndMs
      payDateMs
      periodSource
      hourlyWageCents
      totalMs
      payCents
      ytdPayCents
      entries {
        clockInMs
        clockOutMs
      }
    }
  }
`;

// Paystub email send-history for one period (2026-07-31, admin-only) —
// feeds the Stub Emails panel in Payroll Snapshot Controls.
export const GET_PAYSTUB_EMAIL_LOG = gql`
  query GetPaystubEmailLog($periodEndMs: String!) {
    getPaystubEmailLog(periodEndMs: $periodEndMs) {
      id
      periodEndMs
      userId
      userName
      kind
      email
      status
      trigger
      reportVersion
      correctedAtMs
      error
      sentAtMs
      redirectedTo
    }
  }
`;

export const COMPARE_PAYROLL_SNAPSHOT = gql`
  query ComparePayrollSnapshot($periodEndMs: String!) {
    comparePayrollSnapshot(periodEndMs: $periodEndMs) {
      periodStartMs periodEndMs snapshotExists hasDrift diffJson
      issuedReport { ${PAYROLL_RESULT_BODY} }
      liveReport { ${PAYROLL_RESULT_BODY} }
    }
  }
`;

// Manual YTD adjustments (Phase 8) for a year.
export const GET_PAYROLL_YTD_ADJUSTMENTS = gql`
  query GetPayrollYtdAdjustments($year: String!) {
    getPayrollYtdAdjustments(year: $year) {
      id
      userId
      year
      amountCents
      note
      createdByUserId
      createdAt
    }
  }
`;

// Fix B (2026-05-11) — focused admin query for the Add Advance modal's
// context panel. Returns earnings + existing advances for one user/period
// without paying the full breakdown cost.
export const GET_USER_EARNINGS_FOR_PERIOD = gql`
  query GetUserEarningsForPeriod($userId: String!, $periodEndMs: String!) {
    getUserEarningsForPeriod(userId: $userId, periodEndMs: $periodEndMs) {
      userId
      periodEndMs
      confirmedTotal
      potentialTotal
      hourlyPayCents
      referralFeeTotal
      existingAdvanceTotal
    }
  }
`;

export const GET_USERS_FCM_TOKENS = gql`
  query GetUsersFcmTokens {
    getUsersFcmTokens {
      _id
      firstName
      lastName
      email
      fcmTokens {
        token
        deviceId
        updatedAt
      }
    }
  }
`;

// Door classification review queue (door-gallery classification, 2026-06-11).
// Images intentionally NOT fetched here - rendered via /ba-image/:id.
export const GET_CONFIRMED_CLASSIFICATIONS = gql`
  query GetConfirmedClassifications($count: String, $doorStyleId: String) {
    getConfirmedClassifications(count: $count, doorStyleId: $doorStyleId) {
      _id
      stainColor
      dateEntered
      afterId {
        _id
        s3ImageUrl
        s3ThumbUrl
      }
      classification {
        status
        doorStyleId
        doorStyleFreeform
        classifiedAt
        classifiedByUserId
      }
    }
  }
`;

export const GET_CLASSIFICATION_QUEUE = gql`
  query GetClassificationQueue($count: String) {
    getClassificationQueue(count: $count) {
      _id
      stainColor
      dateEntered
      beforeId {
        _id
        s3ImageUrl
        s3ThumbUrl
      }
      afterId {
        _id
        s3ImageUrl
        s3ThumbUrl
      }
      classification {
        status
        doorStyleId
        doorStyleFreeform
        material
        woodSpecies
        stainGuess
        layout {
          doors
          sidelites
          transom
          jambsFinished
        }
        hasIron
        glassShape
        glassDivision
        needsReview
        suggestion {
          modelVersion
          doorStyleSecond
          doorStyleRaw
          notes
          issues
          confidence {
            doorStyle
            layout
            material
            woodSpecies
          }
        }
      }
    }
  }
`;

export const GET_DOOR_STYLES = gql`
  query GetDoorStyles {
    getDoorStyles {
      _id
      name
      definition
    }
  }
`;

export const GET_CLASSIFICATION_STATS = gql`
  query GetClassificationStats {
    getClassificationStats {
      galleryTotal
      unclassified
      suggested
      confirmed
      needsReview
      enabled
      lifetimeSpendMicroUSD
      nightlyCapMicroUSD
      totalCapMicroUSD
    }
  }
`;

// PUBLIC door browse (Our Doors page, door-gallery classification Phase 3).
export const GET_PUBLIC_DOOR_BROWSE = gql`
  query GetPublicDoorBrowse {
    getPublicDoorBrowse {
      styles {
        _id
        name
        doorCount
      }
      stainColors
      uploadMatchEnabled
    }
  }
`;

export const GET_DOORS_BY_STYLE = gql`
  query GetDoorsByStyle($doorStyleId: String!, $stainColor: String) {
    getDoorsByStyle(doorStyleId: $doorStyleId, stainColor: $stainColor) {
      _id
      stainColor
      beforeId {
        _id
      }
      afterId {
        _id
      }
    }
  }
`;

export const GET_WOOD_SPECIES = gql`
  query GetWoodSpecies {
    getWoodSpecies {
      _id
      name
    }
  }
`;

// Paystub v2 (2026-07-30) — single-user per-bucket period + YTD data for the
// printable earnings statement (PaystubModal on the Payroll Report page).
// periodEndMs may be any instant inside the target Thu→Wed Central period;
// the server snaps it to canonical bounds.
export const GET_PAYSTUB_DATA = gql`
  query getPaystubData($userId: String!, $periodEndMs: String!) {
    getPaystubData(userId: $userId, periodEndMs: $periodEndMs) {
      userId
      firstName
      lastName
      payPercentage
      employeePhone
      employeeAddressLine1
      employeeAddressLine2
      periodStartMs
      periodEndMs
      payDateMs
      periodSource
      period {
        refinishPay
        maintPay
        tipPay
        hcPay
        tripChargePay
        otherPay
        totalPay
      }
      periodCounts {
        refinish
        maint
        hc
        other
        tip
        tripCharge
      }
      jobCount
      advanceTotal
      referralFeeTotal
      netTotal
      ytd {
        refinishPay
        maintPay
        tipPay
        hcPay
        tripChargePay
        otherPay
        totalPay
      }
      ytdAdvanceTotal
      ytdReferralFeeTotal
      ytdNetTotal
      gapJobCount
      gapExcludedCount
    }
  }
`;

// Batch paystubs (2026-07-30) — every pay period in a range in ONE call
// (Generate Multiple). Server clamps to [now - 3 years, current period end]
// and omits zero-pay periods.
export const GET_PAYSTUB_DATA_RANGE = gql`
  query getPaystubDataRange($userId: String!, $startMs: String!, $endMs: String!) {
    getPaystubDataRange(userId: $userId, startMs: $startMs, endMs: $endMs) {
      userId
      firstName
      lastName
      payPercentage
      employeePhone
      employeeAddressLine1
      employeeAddressLine2
      periodStartMs
      periodEndMs
      payDateMs
      periodSource
      period {
        refinishPay
        maintPay
        tipPay
        hcPay
        tripChargePay
        otherPay
        totalPay
      }
      periodCounts {
        refinish
        maint
        hc
        other
        tip
        tripCharge
      }
      jobCount
      advanceTotal
      referralFeeTotal
      netTotal
      ytd {
        refinishPay
        maintPay
        tipPay
        hcPay
        tripChargePay
        otherPay
        totalPay
      }
      ytdAdvanceTotal
      ytdReferralFeeTotal
      ytdNetTotal
      gapJobCount
      gapExcludedCount
    }
  }
`;

// Software Expenses report (2026-09-04) — current-month vendor spend.
// Cents STRINGS; mtd/projected are NULLABLE = render "--", never $0.
export const GET_SOFTWARE_EXPENSES = gql`
  query GetSoftwareExpenses {
    getSoftwareExpenses {
      month
      vendors {
        vendor
        mtdCents
        projectedCents
        basis
        fetchedAtMs
        error
      }
      totalMtdCents
      totalProjectedCents
      updatedAtMs
      twilioTrackedBaseCents
    }
  }
`;

// ── Receipts (inventory hardening Phase 3, plan §3.6) ─────────────────────
// The web reviews and reads receipts captured on mobile (D10 — no web upload).
// Images come back as short-lived signed URLs from the private bucket.
export const RECEIPT_FIELDS = `
      id
      vendor
      purchasedAt
      capturedAt
      uploadedBy
      uploadedByName
      images {
        s3Key
        thumbKey
        order
        width
        height
        url
        thumbUrl
      }
      subtotalCents
      discountCents
      taxCents
      totalCents
      status
      possibleDuplicateOf
      extraction {
        provider
        model
        error
        finishedAt
        attempts
        inputTokens
        outputTokens
        costMicroUSD
      }
      lines {
        lineNo
        rawDesc
        sku
        packHint
        qty
        unitPriceCents
        lineTotalCents
        matchItemId
        matchItemName
        matchSource
        unitsPerPurchase
        fullStockQty
        stockQty
        unitCostCents
        stockedValueCents
        notStockedCents
        priceWarning
        status
        movementId
      }
      sumMismatch {
        lineSumCents
        expectedCents
        diffCents
      }
      confirmedAt
      confirmedBy
      discardedAt
`;

export const GET_RECEIPT = gql`
  query GetReceipt($id: ID!) {
    getReceipt(id: $id) {
      ${RECEIPT_FIELDS}
    }
  }
`;

export const GET_RECEIPTS = gql`
  query GetReceipts($status: String, $dateStart: String, $dateEnd: String, $limit: Int, $cursor: String) {
    getReceipts(status: $status, dateStart: $dateStart, dateEnd: $dateEnd, limit: $limit, cursor: $cursor) {
      items {
        ${RECEIPT_FIELDS}
      }
      nextCursor
    }
  }
`;

// ── Door margin (inventory hardening Phase 4, plan §3.7) ─────────────────
// The report is a stored snapshot (reads never aggregate live). Nested bodies
// are JSON — shape owned by server/utils/doorCostCalc.buildSnapshot.
export const DOOR_COST_SNAPSHOT_FIELDS = `
      id
      computedAt
      localDate
      trigger
      window { startMs endMs }
      marketingWindow { startMs endMs }
      recipeVersions
      doorsDone
      doorPrice
      perKind
      overheadPerDoor
      perRefinisher
      maintenance
      blended
      notes
`;

export const GET_DOOR_COST_SNAPSHOT = gql`
  query GetDoorCostSnapshot($id: ID) {
    getDoorCostSnapshot(id: $id) {
      ${DOOR_COST_SNAPSHOT_FIELDS}
      itemContents
      refinisherSeniority
    }
  }
`;

export const GET_DOOR_COST_SNAPSHOTS = gql`
  query GetDoorCostSnapshots($dateStart: String, $dateEnd: String) {
    getDoorCostSnapshots(dateStart: $dateStart, dateEnd: $dateEnd) {
      id
      computedAt
      localDate
      trigger
      doorsDone
      blended
      perRefinisher
      maintenance
      overheadPerDoor
    }
  }
`;

export const GET_DOOR_MATERIAL_RECIPES = gql`
  query GetDoorMaterialRecipes($jobKind: String) {
    getDoorMaterialRecipes(jobKind: $jobKind) {
      id
      jobKind
      version
      servicesItemId
      servicesItemName
      lines {
        itemId
        itemName
        qtyPerDoor
      }
      effectiveFrom
      createdByName
      note
    }
  }
`;

export const GET_OPERATING_EXPENSES = gql`
  query GetOperatingExpenses($dateStart: String, $dateEnd: String) {
    getOperatingExpenses(dateStart: $dateStart, dateEnd: $dateEnd) {
      id
      category
      sourceOptionId
      sourceName
      amountCents
      periodStart
      periodEnd
      note
      dateCreated
    }
  }
`;

export const GET_INVENTORY_FLAGS = gql`
  query GetInventoryFlags {
    getInventoryFlags {
      inventoryReminderEnabled
      doorCostSnapshotEnabled
    }
  }
`;

// Inventory Spend summary (web: Business Ledger → Inventory → Purchases; the
// mobile Inventory Spend report). `receive` rows' stocked value split by cost
// source, plus receipt-level amounts so it reconciles to the receipts.
export const GET_INVENTORY_SPEND_SUMMARY = gql`
  query GetInventorySpendSummary($dateStart: String!, $dateEnd: String!, $groupBy: String) {
    getInventorySpendSummary(dateStart: $dateStart, dateEnd: $dateEnd, groupBy: $groupBy) {
      buckets {
        key
        label
        confirmedCents
        estimatedCents
        receiveCount
      }
      confirmedCents
      estimatedCents
      notStockedCents
      discountCents
      taxCents
    }
  }
`;

// Inventory Flow report (plan_inventory_cost_reporting_2026_10_01 §A/§B):
// opening → purchased / returned / issued / losses / corrections / baseline /
// unexplained → closing, totals + per item. All numbers are strings of cents / qty.
const INVENTORY_FLOW_FIELDS = `
  receivedQty receivedCents receivedConfirmedCents receivedEstimatedCents
  returnedQty returnedCents
  issuedQty issuedCents
  correctionUpQty correctionUpCents correctionDownQty correctionDownCents
  damagedQty damagedCents
  shrinkageQty shrinkageCents
  baselineQty baselineCents
  unexplainedQty unexplainedCents
  lossesCents
`;
export const GET_INVENTORY_FLOW_REPORT = gql`
  query GetInventoryFlowReport($dateStart: String!, $dateEnd: String!) {
    getInventoryFlowReport(dateStart: $dateStart, dateEnd: $dateEnd) {
      dateStart
      dateEnd
      totals {
        openingValueCents
        closingValueCents
        ${INVENTORY_FLOW_FIELDS}
        unpricedItemCount
        revaluedRowCount
        driftedItemCount
        orphanRowCount
      }
      items {
        itemId
        itemName
        stockUnit
        active
        priceCents
        openingQty
        closingQty
        openingValueCents
        closingValueCents
        ${INVENTORY_FLOW_FIELDS}
        moved
        unpriced
        revaluedRowCount
        ledgerDriftQty
      }
    }
  }
`;

// Refinisher picker for the Shop Inventory Adjust dialog. Lean on purpose —
// GET_ALL_USERS drags every user's text-message bodies and pay fields.
export const GET_INVENTORY_TECHS = gql`
  query GetInventoryTechs {
    getAllUsers {
      _id
      firstName
      lastName
      permission
      active
    }
  }
`;

/**
 * GET_AVAILABLE_SOONER_EVENTS — the Tasks console's "Avail. Sooner" column.
 *
 * TAKES NO ARGUMENTS, and that is the design. The server owns the date window;
 * a client-supplied bound would be unclamped, and "the query hides past
 * events" is the whole stale-flag strategy (there is no sweeper). It also
 * means both mount sites of the panel — mobile and desktop — issue an
 * identical request, so Apollo's queryDeduplication collapses them to one.
 *
 * DELIBERATELY NARROW. This is the post-login landing page, where the binding
 * rule is that the document must not pull invoices, quotes, phone numbers or
 * client notes. A row renders a name, an address, a date and two buttons —
 * nothing here that a row does not draw.
 */
export const GET_AVAILABLE_SOONER_EVENTS = gql`
  query GetAvailableSoonerEvents {
    getAvailableSoonerEvents {
      calendarItem {
        id
        startMs
        eventType
      }
      customer {
        _id
        firstName
        lastName
        streetAddress
        city
        state
        zipCode
      }
    }
  }
`;

/** The signed-in user's private note. No userId — the server reads context. */
export const GET_MY_TASK_NOTE = gql`
  query GetMyTaskNote {
    getMyTaskNote
  }
`;

/**
 * Drive legs from the pinned start to each stop and from each stop to the
 * pinned end (2026-10-09). One call serves both the compare modal (one stop)
 * and the Available Sooner list (every row as a stop).
 */
export const GET_DRIVE_LEGS = gql`
  query GetDriveLegs(
    $start: DriveAddressInput
    $end: DriveAddressInput
    $stops: [DriveAddressInput!]!
  ) {
    getDriveLegs(start: $start, end: $end, stops: $stops) {
      budgetExhausted
      elementsSent
      stops {
        fromStart {
          ok
          distanceMeters
          durationSec
        }
        toEnd {
          ok
          distanceMeters
          durationSec
        }
      }
    }
  }
`;
